// BrandingView — admin edita identidade visual da plataforma sem deploy.
//
// Edita em platform_config key="branding":
//   - Nome da plataforma (aparece no <title>)
//   - URL do favicon
//   - URL do logo
//   - Cor primária (--brand-primary)
//   - Labels dos portais (Aluno, Professor, Org, Admin, Entrar)
//   - Footer text
//
// O endpoint público /api/public/branding aplica imediatamente nos clients —
// o signal global no apps/ui/lib/branding propaga sem reload.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Palette,
  RotateCcw,
  Save
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { api, ApiError, type BrandingConfig } from "./api";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const DEFAULTS: BrandingConfig = {
  platformName: "Simulador",
  faviconUrl: null,
  logoUrl: null,
  primaryColor: "#8b5cf6",
  portalLabels: {
    student: "Aluno",
    teacher: "Professor",
    company: "Organização",
    manager: "Administrador",
    auth: "Entrar"
  },
  loginSubtitle: "Estude, simule provas e acompanhe sua evolução.",
  footerText: null,
  updatedAt: null,
  updatedBy: null
};

interface State {
  phase: "idle" | "loading" | "ready" | "saving" | "error";
  config: BrandingConfig;
  dirty: boolean;
  errMsg: string | null;
  successMsg: string | null;
}

const state = signal<State>({
  phase: "idle",
  config: DEFAULTS,
  dirty: false,
  errMsg: null,
  successMsg: null
});

async function load() {
  state.value = { ...state.value, phase: "loading", errMsg: null };
  try {
    const { config } = await api.getBranding();
    state.value = {
      phase: "ready",
      config,
      dirty: false,
      errMsg: null,
      successMsg: null
    };
  } catch (err) {
    state.value = {
      ...state.value,
      phase: "error",
      errMsg: friendlyError(err)
    };
  }
}

function patch(partial: Partial<BrandingConfig>) {
  state.value = {
    ...state.value,
    config: { ...state.value.config, ...partial },
    dirty: true,
    successMsg: null
  };
}

function patchLabel(key: keyof BrandingConfig["portalLabels"], value: string) {
  state.value = {
    ...state.value,
    config: {
      ...state.value.config,
      portalLabels: { ...state.value.config.portalLabels, [key]: value }
    },
    dirty: true,
    successMsg: null
  };
}

async function save() {
  const cur = state.value.config;
  state.value = { ...state.value, phase: "saving", errMsg: null };
  try {
    const { config } = await api.saveBranding({
      platformName: cur.platformName,
      faviconUrl: cur.faviconUrl,
      logoUrl: cur.logoUrl,
      primaryColor: cur.primaryColor,
      portalLabels: cur.portalLabels,
      loginSubtitle: cur.loginSubtitle,
      footerText: cur.footerText
    });
    state.value = {
      phase: "ready",
      config,
      dirty: false,
      errMsg: null,
      successMsg: "Branding salvo. Recarregue o site pra ver."
    };
    setTimeout(() => {
      if (state.value.successMsg)
        state.value = { ...state.value, successMsg: null };
    }, 4000);
  } catch (err) {
    const msg =
      err instanceof ApiError
        ? err.message
        : err instanceof Error
          ? err.message
          : String(err);
    state.value = { ...state.value, phase: "ready", errMsg: msg };
  }
}

export function BrandingView() {
  useEffect(() => {
    void load();
  }, []);

  const { phase, config, dirty, errMsg, successMsg } = state.value;

  return (
    <div class="space-y-5">
      <header class="flex items-start justify-between gap-3">
        <div>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Palette className="size-6 text-primary" />
            Identidade visual
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Mude nome, favicon, cores e labels dos portais sem precisar
            redeployar. Mudanças surtem efeito em até 30 segundos pra todos os
            usuários ativos.
          </p>
        </div>
        {dirty && phase !== "saving" && (
          <button
            type="button"
            onClick={() => void save()}
            class="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
          >
            <Save className="size-4" />
            Salvar mudanças
          </button>
        )}
        {phase === "saving" && (
          <span class="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Salvando…
          </span>
        )}
      </header>

      {errMsg && (
        <div class="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>{errMsg}</span>
        </div>
      )}
      {successMsg && (
        <div class="flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {phase === "loading" || phase === "idle" ? (
        <div class="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : (
        <>
          {/* Card: Identidade básica */}
          <section class="rounded-xl border bg-card p-6 shadow-sm">
            <h2 class="text-lg font-semibold">Identidade básica</h2>
            <p class="mt-1 text-sm text-muted-foreground">
              Nome aparece no título da aba e em headers. Cor primária pinta
              botões e destaques.
            </p>
            <div class="mt-4 grid gap-4 sm:grid-cols-2">
              <label class="block">
                <span class="block text-sm font-medium">Nome da plataforma</span>
                <input
                  type="text"
                  value={config.platformName}
                  onInput={(e) =>
                    patch({
                      platformName: (e.target as HTMLInputElement).value
                    })
                  }
                  maxLength={80}
                  class="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Simulador"
                />
              </label>
              <label class="block">
                <span class="block text-sm font-medium">Cor primária</span>
                <div class="mt-1 flex items-center gap-2">
                  <input
                    type="color"
                    value={config.primaryColor}
                    onInput={(e) =>
                      patch({
                        primaryColor: (e.target as HTMLInputElement).value
                      })
                    }
                    class="h-10 w-16 cursor-pointer rounded border"
                  />
                  <input
                    type="text"
                    value={config.primaryColor}
                    onInput={(e) =>
                      patch({
                        primaryColor: (e.target as HTMLInputElement).value
                      })
                    }
                    pattern="^#[0-9a-fA-F]{6}$"
                    class="flex-1 rounded-md border bg-background px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="#8b5cf6"
                  />
                </div>
              </label>
              <label class="block sm:col-span-2">
                <span class="block text-sm font-medium">URL do favicon</span>
                <input
                  type="url"
                  value={config.faviconUrl ?? ""}
                  onInput={(e) =>
                    patch({
                      faviconUrl:
                        (e.target as HTMLInputElement).value.trim() || null
                    })
                  }
                  class="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="https://exemplo.com/favicon.png ou deixe vazio pro default"
                />
                <span class="mt-1 block text-xs text-muted-foreground">
                  Recomendado: PNG 256×256 ou maior. Em branco usa /favicon.png
                  (atual: logo-1024).
                </span>
              </label>
              <label class="block sm:col-span-2">
                <span class="block text-sm font-medium">URL do logo</span>
                <input
                  type="url"
                  value={config.logoUrl ?? ""}
                  onInput={(e) =>
                    patch({
                      logoUrl:
                        (e.target as HTMLInputElement).value.trim() || null
                    })
                  }
                  class="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="https://exemplo.com/logo.svg"
                />
              </label>
            </div>
          </section>

          {/* Card: Labels dos portais */}
          <section class="rounded-xl border bg-card p-6 shadow-sm">
            <h2 class="text-lg font-semibold">Labels dos portais</h2>
            <p class="mt-1 text-sm text-muted-foreground">
              Como cada portal aparece nos menus e títulos. Mantém em PT-BR ou
              traduz pra outra língua.
            </p>
            <div class="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {(
                [
                  ["student", "Aluno"],
                  ["teacher", "Professor"],
                  ["company", "Organização"],
                  ["manager", "Administrador"],
                  ["auth", "Entrar"]
                ] as const
              ).map(([key, defaultLabel]) => (
                <label key={key} class="block">
                  <span class="block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {defaultLabel}
                  </span>
                  <input
                    type="text"
                    value={config.portalLabels[key]}
                    onInput={(e) =>
                      patchLabel(
                        key,
                        (e.target as HTMLInputElement).value || defaultLabel
                      )
                    }
                    maxLength={40}
                    class="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </label>
              ))}
            </div>
          </section>

          {/* Card: Textos da página de login */}
          <section class="rounded-xl border bg-card p-6 shadow-sm">
            <h2 class="text-lg font-semibold">Textos da página de login</h2>
            <p class="mt-1 text-sm text-muted-foreground">
              Subtítulo e rodapé que aparecem abaixo do título "Simulador" e no
              rodapé da página de login.
            </p>
            <div class="mt-4 space-y-4">
              <label class="block">
                <span class="block text-sm font-medium">
                  Subtítulo (abaixo do título "Simulador")
                </span>
                <input
                  type="text"
                  value={config.loginSubtitle ?? ""}
                  onInput={(e) =>
                    patch({
                      loginSubtitle:
                        (e.target as HTMLInputElement).value.trim() || null
                    })
                  }
                  maxLength={200}
                  class="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Estude, simule provas e acompanhe sua evolução."
                />
                <span class="mt-1 block text-xs text-muted-foreground">
                  Em branco usa o default. Limite: 200 caracteres.
                </span>
              </label>
              <label class="block">
                <span class="block text-sm font-medium">
                  Rodapé (todas as páginas públicas — login, signup, pay)
                </span>
                <textarea
                  value={config.footerText ?? ""}
                  onInput={(e) =>
                    patch({
                      footerText:
                        (e.target as HTMLTextAreaElement).value.trim() || null
                    })
                  }
                  rows={3}
                  class="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="© 2026 Sua Empresa. Todos os direitos reservados."
                />
                <span class="mt-1 block text-xs text-muted-foreground">
                  Em branco esconde o rodapé.
                </span>
              </label>
            </div>
          </section>

          {/* Preview */}
          <section class="rounded-xl border bg-card p-6 shadow-sm">
            <h2 class="text-lg font-semibold">Preview</h2>
            <div class="mt-4 rounded-lg border p-4">
              <div class="flex items-center gap-3">
                {config.logoUrl ? (
                  <img
                    src={config.logoUrl}
                    alt="logo"
                    class="h-8 w-8 rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <div
                    class="h-8 w-8 rounded"
                    style={{ background: config.primaryColor }}
                  />
                )}
                <span class="text-lg font-bold">{config.platformName}</span>
              </div>
              <div class="mt-3 flex flex-wrap gap-2">
                {Object.entries(config.portalLabels).map(([key, label]) => (
                  <span
                    key={key}
                    class="rounded-full border px-3 py-1 text-xs font-medium"
                    style={{
                      borderColor: config.primaryColor,
                      color: config.primaryColor
                    }}
                  >
                    {label}
                  </span>
                ))}
              </div>
              {config.footerText && (
                <p class="mt-3 text-xs text-muted-foreground">
                  {config.footerText}
                </p>
              )}
            </div>
          </section>

          {/* Reset */}
          <section class="flex items-center justify-between rounded-xl border bg-muted/30 p-4 text-sm">
            <span class="text-muted-foreground">
              {config.updatedAt
                ? `Última edição: ${new Date(config.updatedAt).toLocaleString("pt-BR")}${
                    config.updatedBy ? ` por ${config.updatedBy}` : ""
                  }`
                : "Sem edições — usando valores padrão"}
            </span>
            <button
              type="button"
              onClick={() => {
                state.value = {
                  ...state.value,
                  config: { ...DEFAULTS, updatedAt: config.updatedAt, updatedBy: config.updatedBy },
                  dirty: true
                };
              }}
              class="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs hover:bg-background"
            >
              <RotateCcw className="size-3" />
              Restaurar defaults
            </button>
          </section>
        </>
      )}
    </div>
  );
}
